import React, { ReactElement, Fragment, Component, RefObject, createRef } from 'react'
import ReactVisibilitySensor from 'react-visibility-sensor'
import FlatListProps, { ViewToken, Offset } from '../FlatListProps'
import Grid from '../../Grid'

type VisibilitySensorProps<ItemT> = Pick<ViewToken<ItemT>, 'item' | 'index'> &
    Pick<FlatListProps<ItemT>, 'keyExtractor'> & {
        index: number
        horizontal: boolean
        children?: ReactElement | (ReactElement | null)[]
        onViewableItemsChanged?: (changed: ViewToken<ItemT>) => void
        containment?: HTMLDivElement | null
        minimumViewTime?: number
        itemVisiblePercentThreshold?: number
    }

class ListVisibilitySensor<ItemT> extends Component<VisibilitySensorProps<ItemT>> {
    initialRender = true
    itemRef: RefObject<HTMLDivElement | null> = createRef()

    onChange = (isViewable: boolean): void => {
        const { keyExtractor, item, index, onViewableItemsChanged } = this.props
        const key = keyExtractor(item, index)
        if (onViewableItemsChanged && !(this.initialRender && !isViewable)) {
            onViewableItemsChanged({
                key,
                index,
                item,
                isViewable,
            })
        }
        this.initialRender = false
    }

    createOffset = (
        itemVisiblePercentThreshold: number | undefined,
        horizontal: boolean
    ): Offset => {
        let offset = { top: -10, bottom: -10, left: -10, right: -10 }
        if (itemVisiblePercentThreshold && this.itemRef.current) {
            const width = this.itemRef.current.getBoundingClientRect().width
            const offsetValue = (width * itemVisiblePercentThreshold) / 100
            offset = horizontal
                ? { top: -10, bottom: -10, left: offsetValue, right: offsetValue }
                : { top: offsetValue, bottom: offsetValue, left: -10, right: -10 }
        }
        return offset
    }

    render(): ReactElement {
        const {
            onViewableItemsChanged,
            containment,
            children,
            minimumViewTime,
            itemVisiblePercentThreshold,
            horizontal,
        } = this.props

        return onViewableItemsChanged && containment ? (
            <Grid noExpand ref={this.itemRef} horizontal={horizontal}>
                <ReactVisibilitySensor
                    onChange={this.onChange}
                    containment={containment}
                    intervalCheck={false}
                    scrollCheck
                    scrollThrottle={100}
                    resizeCheck
                    offset={this.createOffset(itemVisiblePercentThreshold, horizontal)}
                    intervalDelay={minimumViewTime}
                    partialVisibility={!!itemVisiblePercentThreshold}
                >
                    <Fragment>{children}</Fragment>
                </ReactVisibilitySensor>
            </Grid>
        ) : (
            <Fragment>{children}</Fragment>
        )
    }
}

const typedMemo: <T>(c: T) => T = React.memo

export default typedMemo(ListVisibilitySensor)
